import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch, supabase } from '../../lib/supabase';
import { DEFAULT_PRICE_TABLE } from '../../config/constants';
import { useTenantConfig } from '../../hooks/useTenant';
import { F, C, btn } from '../../config/styles';
import { buildCSV, buildMetafieldsCSV, buildFitFabricCSV, buildCareInstructionsCSV, buildWarehouseCSV, warehouseTerritories, downloadCSV, validateProducts, translateVariantSize, DEFAULT_REQUIRED_EXPORT_FIELDS } from '../../utils/csv';

export function ExportView({ products, collectionId, priceTable = DEFAULT_PRICE_TABLE, sizeTable = null, onUpdateProducts, currentUser }) {
  const tenantConfig = useTenantConfig();
  const { territories, deptIds } = tenantConfig;
  const [downloaded, setDownloaded]             = useState({});
  const [stores, setStores]                     = useState([]);
  const [shopifyPushing, setShopifyPushing]     = useState(false);
  const [shopifyPushResult, setShopifyPushResult] = useState(null);
  const [warehousePushing, setWarehousePushing]   = useState(false);
  const [warehousePushResult, setWarehousePushResult] = useState(null);
  const [selectedStoreTerr, setSelectedStoreTerr] = useState('ALL');
  const [shopifySyncing, setShopifySyncing]     = useState(false);
  const [syncingAll, setSyncingAll]             = useState(false);
  const [pushOrder, setPushOrder]               = useState('top');
  const [warehousePushOrder, setWarehousePushOrder] = useState('top');
  const [warehouseStoreTerr, setWarehouseStoreTerr] = useState('ALL');
  const [warehouseSyncing, setWarehouseSyncing]     = useState(false);
  const [syncError, setSyncError]                   = useState(null);
  const [warehouseSyncError, setWarehouseSyncError] = useState(null);
  const [warehouseIncludeImages, setWarehouseIncludeImages] = useState(false);
  const [warehouseIncludeBody, setWarehouseIncludeBody]     = useState(false);
  const [updateExisting, setUpdateExisting]               = useState(false);
  const [updateWarehouseExisting, setUpdateWarehouseExisting] = useState(false);
  const [exportAsIs, setExportAsIs]                       = useState(false);

  useEffect(() => {
    apiFetch('/api/shopify/stores')
      .then(r => r.json())
      .then(d => {
        if (d.stores?.length) {
          setStores(d.stores);
        }
      })
      .catch(() => {});
  }, []);

  const pushToShopify = async () => {
    setShopifyPushing(true);
    setShopifyPushResult(null);
    let totalPushed = 0;
    const allErrors = [];
    try {
      const freshStores = await apiFetch('/api/shopify/stores').then(r => r.json()).then(d => d.stores || stores);
      setStores(freshStores);
      for (const store of freshStores) {
        const terrCode = selectedStoreTerr === 'ALL' ? (store.territory || 'UK') : selectedStoreTerr;
        const terrObj = territories.find(t => t.code === terrCode);
        const storeDomain = store.store_domain;
        const toPush = updateExisting
          ? [...approved]
          : approved.filter(p => !p.shopifyConnections?.[storeDomain]);
        if (pushOrder === 'bottom') toPush.reverse();
        if (!toPush.length) continue;
        const translatedProducts = toPush.map(p => {
          const tr = p.translations?.[terrCode] || {};
          return {
            ...p,
            title:    tr.title    || p.title,
            bodyHtml: tr.bodyHtml || p.bodyHtml,
            variants: p.variants.map(v => {
              const { opt1Val, opt2Val } = translateVariantSize(
                v.opt1Val || '', v.opt2Name || '', v.opt2Val || '', terrObj, sizeTable, tenantConfig?.sizeCategories
              );
              const territoryPrice = p.territoryPrices?.[terrCode];
              return { ...v, opt1Val, opt2Val, price: territoryPrice || v.price || p.basePrice };
            }),
          };
        });
        const res = await apiFetch('/api/shopify/push', {
          method: 'POST',
          body: JSON.stringify({ products: translatedProducts, storeDomain, namespace: tenantConfig?.metafieldNamespace || tenantConfig?.subdomain || 'store', metaFields: tenantConfig?.metafields || [], fieldMappings: tenantConfig?.fieldMappings || {}, removedFields: tenantConfig?.disabledFields || [] }),
        });
        const data = await res.json();
        if (!res.ok) { allErrors.push({ error: data.error || `Push failed for ${storeDomain}` }); continue; }
        if (onUpdateProducts) {
          const toPushIds = new Set(toPush.map(p => p.id));
          const connMap = Object.fromEntries((data.results || []).map(r => [r.hubId, r]));
          const handleMap = Object.fromEntries((data.results || []).map(r => [r.handle, r]));
          const now = new Date().toISOString();
          const updatedProducts = products.map(p => {
            if (!toPushIds.has(p.id)) return p;
            const r = connMap[p.id] || handleMap[p.handle];
            return {
              ...p,
              shopifyConnections: {
                ...(p.shopifyConnections || {}),
                [storeDomain]: { shopifyId: r?.shopifyId || '', handle: r?.handle || p.handle, adminUrl: r?.adminUrl || '', status: 'draft', pushedAt: now, pushedBy: currentUser?.email || '' },
              },
            };
          });
          onUpdateProducts(updatedProducts);
          if (collectionId) {
            const ts = new Date().toISOString();
            await Promise.all(updatedProducts.map(p => supabase.from('products').upsert({ id: p.id, collection_id: collectionId, data: p, updated_at: ts }, { onConflict: 'id' })));
          }
        }
        totalPushed += data.results?.length ?? 0;
        if (data.errors?.length) allErrors.push(...data.errors);
      }
      setShopifyPushResult({ pushed: totalPushed, errors: allErrors });
    } catch (err) {
      setShopifyPushResult({ pushed: totalPushed, errors: [{ error: err.message }] });
    } finally {
      setShopifyPushing(false);
    }
  };

  const pushWarehouseToShopify = async () => {
    setWarehousePushing(true);
    setWarehousePushResult(null);
    let totalPushed = 0;
    const allErrors = [];
    try {
      const freshStores = await apiFetch('/api/shopify/stores').then(r => r.json()).then(d => d.stores || stores);
      setStores(freshStores);
      for (const store of freshStores) {
        const terrCode = warehouseStoreTerr === 'ALL' ? (store.territory || 'UK') : warehouseStoreTerr;
        const terrObj = territories.find(t => t.code === terrCode);
        const storeDomain = store.store_domain;
        const toPush = updateWarehouseExisting
          ? [...warehouseReady]
          : warehouseReady.filter(p => !p.warehouseConnections?.[storeDomain]);
        if (warehousePushOrder === 'bottom') toPush.reverse();
        if (!toPush.length) continue;
        const translatedProducts = toPush.map(p => {
          const tr = p.translations?.[terrCode] || {};
          return {
            ...p,
            title:    tr.title    || p.title,
            bodyHtml: tr.bodyHtml || p.bodyHtml,
            variants: p.variants.map(v => {
              const { opt1Val, opt2Val } = translateVariantSize(
                v.opt1Val || '', v.opt2Name || '', v.opt2Val || '', terrObj, sizeTable, tenantConfig?.sizeCategories
              );
              const territoryPrice = p.territoryPrices?.[terrCode];
              return { ...v, opt1Val, opt2Val, price: territoryPrice || v.price || p.basePrice };
            }),
          };
        });
        const res = await apiFetch('/api/shopify/push', {
          method: 'POST',
          body: JSON.stringify({ products: translatedProducts, storeDomain, isWarehouse: true, namespace: tenantConfig?.metafieldNamespace || tenantConfig?.subdomain || 'store', metaFields: tenantConfig?.metafields || [], fieldMappings: tenantConfig?.fieldMappings || {}, removedFields: tenantConfig?.disabledFields || [] }),
        });
        const data = await res.json();
        if (!res.ok) { allErrors.push({ error: data.error || `Push failed for ${storeDomain}` }); continue; }
        if (onUpdateProducts) {
          const toPushIds = new Set(toPush.map(p => p.id));
          const connMap = Object.fromEntries((data.results || []).map(r => [r.hubId, r]));
          const handleMap = Object.fromEntries((data.results || []).map(r => [r.handle, r]));
          const now = new Date().toISOString();
          const updatedProducts = products.map(p => {
            if (!toPushIds.has(p.id)) return p;
            const r = connMap[p.id] || handleMap[p.handle];
            return {
              ...p,
              warehouseConnections: {
                ...(p.warehouseConnections || {}),
                [storeDomain]: { shopifyId: r?.shopifyId || '', handle: r?.handle || p.handle, adminUrl: r?.adminUrl || '', status: 'draft', pushedAt: now, pushedBy: currentUser?.email || '' },
              },
            };
          });
          onUpdateProducts(updatedProducts);
          if (collectionId) {
            const ts = new Date().toISOString();
            await Promise.all(updatedProducts.map(p => supabase.from('products').upsert({ id: p.id, collection_id: collectionId, data: p, updated_at: ts }, { onConflict: 'id' })));
          }
        }
        totalPushed += data.results?.length ?? 0;
        if (data.errors?.length) allErrors.push(...data.errors);
      }
      setWarehousePushResult({ pushed: totalPushed, errors: allErrors });
    } catch (err) {
      setWarehousePushResult({ pushed: totalPushed, errors: [{ error: err.message }] });
    } finally {
      setWarehousePushing(false);
    }
  };

  const syncAllStores = async () => {
    setSyncingAll(true);
    setShopifySyncing(true);
    setSyncError(null);
    let updatedProducts = [...products];
    const storeErrors = [];
    const now = new Date().toISOString();
    try {
      for (const store of stores) {
        const storeDomain = store.store_domain;
        const pushed    = updatedProducts.filter(p => p.shopifyConnections?.[storeDomain]?.shopifyId);
        const untracked = updatedProducts.filter(p => p.handle && !p.shopifyConnections?.[storeDomain]);
        if (!pushed.length && !untracked.length) continue;
        try {
          const res = await apiFetch('/api/shopify/sync', {
            method: 'POST',
            body: JSON.stringify({
              shopifyIds: pushed.map(p => p.shopifyConnections[storeDomain].shopifyId),
              handles:    untracked.map(p => p.handle),
              storeDomain,
            }),
          });
          const data = await res.json();
          if (!res.ok) { storeErrors.push(`${storeDomain}: ${data.error || `HTTP ${res.status}`}`); continue; }

          // Update status for already-tracked products
          const statusMap = Object.fromEntries((data.results || []).filter(r => !r.error).map(r => [r.shopifyId, r]));
          const deletedIds = new Set((data.results || []).filter(r => 'error' in r).map(r => r.shopifyId));
          updatedProducts = updatedProducts.map(p => {
            const conn = p.shopifyConnections?.[storeDomain];
            if (!conn) return p;
            if (deletedIds.has(conn.shopifyId)) {
              const newConns = { ...(p.shopifyConnections || {}) };
              delete newConns[storeDomain];
              return { ...p, shopifyConnections: newConns };
            }
            const s = statusMap[conn.shopifyId];
            if (!s) return p;
            return {
              ...p,
              shopifyConnections: {
                ...(p.shopifyConnections || {}),
                [storeDomain]: { ...conn, status: s.status, publishedAt: s.publishedAt, syncedAt: now },
              },
            };
          });

          // Establish connections for newly discovered products
          const discovered = Object.fromEntries(
            (data.handleResults || []).filter(r => !r.error).map(r => [r.handle, r])
          );
          updatedProducts = updatedProducts.map(p => {
            const found = discovered[p.handle];
            if (!found) return p;
            return {
              ...p,
              shopifyConnections: {
                ...(p.shopifyConnections || {}),
                [storeDomain]: { shopifyId: found.shopifyId, handle: found.handle, adminUrl: found.adminUrl, status: found.status, pushedAt: now, pushedBy: '' },
              },
            };
          });
        } catch (e) { storeErrors.push(`${storeDomain}: ${e.message}`); }
      }
      if (onUpdateProducts) onUpdateProducts(updatedProducts);
      if (collectionId && updatedProducts.length) {
        const ts = new Date().toISOString();
        await Promise.all(updatedProducts.map(p => supabase.from('products').upsert({ id: p.id, collection_id: collectionId, data: p, updated_at: ts }, { onConflict: 'id' })));
      }
      if (storeErrors.length) setSyncError(storeErrors.join(' · '));
    } finally {
      setShopifySyncing(false);
      setSyncingAll(false);
    }
  };

  const syncWarehouseStores = async () => {
    setWarehouseSyncing(true);
    setWarehouseSyncError(null);
    let updatedProducts = [...products];
    const storeErrors = [];
    const now = new Date().toISOString();
    try {
      for (const store of stores) {
        const storeDomain = store.store_domain;
        const pushed    = updatedProducts.filter(p => p.warehouseConnections?.[storeDomain]?.shopifyId);
        const untracked = updatedProducts.filter(p => p.handle && !p.warehouseConnections?.[storeDomain]);
        if (!pushed.length && !untracked.length) continue;
        try {
          const res = await apiFetch('/api/shopify/sync', {
            method: 'POST',
            body: JSON.stringify({
              shopifyIds: pushed.map(p => p.warehouseConnections[storeDomain].shopifyId),
              handles:    untracked.map(p => p.handle),
              storeDomain,
            }),
          });
          const data = await res.json();
          if (!res.ok) { storeErrors.push(`${storeDomain}: ${data.error || `HTTP ${res.status}`}`); continue; }

          // Update status for already-tracked products
          const deletedIds = new Set((data.results || []).filter(r => 'error' in r).map(r => r.shopifyId));
          updatedProducts = updatedProducts.map(p => {
            const conn = p.warehouseConnections?.[storeDomain];
            if (!conn) return p;
            if (deletedIds.has(conn.shopifyId)) {
              const newConns = { ...(p.warehouseConnections || {}) };
              delete newConns[storeDomain];
              return { ...p, warehouseConnections: newConns };
            }
            return p;
          });

          // Establish connections for newly discovered products
          const discovered = Object.fromEntries(
            (data.handleResults || []).filter(r => !r.error).map(r => [r.handle, r])
          );
          updatedProducts = updatedProducts.map(p => {
            const found = discovered[p.handle];
            if (!found) return p;
            return {
              ...p,
              warehouseConnections: {
                ...(p.warehouseConnections || {}),
                [storeDomain]: { shopifyId: found.shopifyId, handle: found.handle, adminUrl: found.adminUrl, status: found.status, pushedAt: now, pushedBy: '' },
              },
            };
          });
        } catch (e) { storeErrors.push(`${storeDomain}: ${e.message}`); }
      }
      if (onUpdateProducts) onUpdateProducts(updatedProducts);
      if (collectionId && updatedProducts.length) {
        const ts = new Date().toISOString();
        await Promise.all(updatedProducts.map(p => supabase.from('products').upsert({ id: p.id, collection_id: collectionId, data: p, updated_at: ts }, { onConflict: 'id' })));
      }
      if (storeErrors.length) setWarehouseSyncError(storeErrors.join(' · '));
    } finally {
      setWarehouseSyncing(false);
    }
  };
  const today = new Date().toISOString().slice(0, 10);

  const warehouseReady = useMemo(() =>
    products.filter(p => p.handle && p.title && p.variants?.some(v => v.sku)), [products]);

  const doExportWarehouse = () => {
    const opts  = { includeImages: warehouseIncludeImages, includeBody: warehouseIncludeBody };
    const terrs = warehouseTerritories(warehouseReady, territories);
    if (terrs.length > 0) {
      terrs.forEach(t => {
        downloadCSV(buildWarehouseCSV(warehouseReady, t, priceTable, sizeTable, opts, tenantConfig), `warehouse-draft-${t.code.toLowerCase()}-${today}.csv`);
      });
    } else {
      downloadCSV(buildWarehouseCSV(warehouseReady, null, priceTable, sizeTable, opts, tenantConfig), `warehouse-draft-${today}.csv`);
    }
    setDownloaded(prev => ({ ...prev, _warehouse: true }));
  };

  const approved = useMemo(() =>
    products.filter(p => deptIds.every(d => p.sectionStatus[d] === 'approved')), [products, deptIds]);

  // "Export as-is" override target: every product with at least some identifying content, ignoring
  // approval status and required-field validation. Blank cells simply export blank.
  const withContent = useMemo(() =>
    products.filter(p => p.handle || p.skuBase || p.title), [products]);

  const { errors, warnings, excludedIds } = useMemo(() => {
    const disabled = tenantConfig?.disabledFields || [];
    const requiredFields = (tenantConfig?.exportRequiredFields || DEFAULT_REQUIRED_EXPORT_FIELDS)
      .filter(f => !disabled.includes(f)); // a disabled field can never block export — nothing can fill it in
    return validateProducts(approved, requiredFields);
  }, [approved, tenantConfig?.exportRequiredFields, tenantConfig?.disabledFields]);
  const exportable = useMemo(
    () => exportAsIs ? withContent : approved.filter(p => !excludedIds.has(p.id)),
    [exportAsIs, withContent, approved, excludedIds]
  );

  const pushedToSelected   = useMemo(() => approved.filter(p => stores.every(s => p.shopifyConnections?.[s.store_domain])), [approved, stores]);
  const unpushedToSelected = useMemo(() => approved.filter(p => stores.some(s => !p.shopifyConnections?.[s.store_domain])), [approved, stores]);

  const warehousePushedToStore   = useMemo(() => warehouseReady.filter(p => stores.every(s => p.warehouseConnections?.[s.store_domain])), [warehouseReady, stores]);
  const warehouseUnpushedToStore = useMemo(() => warehouseReady.filter(p => stores.some(s => !p.warehouseConnections?.[s.store_domain])), [warehouseReady, stores]);

  const ns = tenantConfig?.subdomain || 'export';
  const doExport = (t) => {
    const csv = buildCSV(exportable, t, priceTable, sizeTable, tenantConfig);
    downloadCSV(csv, `${ns}-product-build-${t.code.toLowerCase()}-${today}.csv`);
    setDownloaded(prev => ({ ...prev, [t.code]: true }));
  };

  const doExportMeta = () => {
    downloadCSV(buildMetafieldsCSV(exportable, tenantConfig), `${ns}-metafields-${today}.csv`);
    setDownloaded(prev => ({ ...prev, _meta: true }));
  };

  const doExportFit = () => {
    downloadCSV(buildFitFabricCSV(exportable, tenantConfig), `${ns}-fit-fabric-${today}.csv`);
    setDownloaded(prev => ({ ...prev, _fit: true }));
  };

  const doExportCare = () => {
    downloadCSV(buildCareInstructionsCSV(exportable, tenantConfig), `${ns}-care-instructions-${today}.csv`);
    setDownloaded(prev => ({ ...prev, _care: true }));
  };

  const exportAll = () => {
    territories.forEach(t => doExport(t));
    doExportMeta();
    doExportFit();
    doExportCare();
  };

  const statCard = (label, val) => (
    <div style={{ background: C.fa, border: `1px solid ${C.e8}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontFamily: F.display, fontSize: 28, color: C.text }}>{val}</div>
      <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{label}</div>
    </div>
  );

  return (
    <div style={{ padding: '28px 32px', maxWidth: 940, margin: '0 auto' }}>
      <div style={{ fontFamily: F.display, fontSize: 30, color: C.text, marginBottom: 28 }}>Export</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
        {statCard('Total products',  products.length)}
        {statCard('Warehouse ready', warehouseReady.length)}
        {statCard('Fully approved',  approved.length)}
        {statCard('Export ready',    exportable.length)}
      </div>

      {/* ── Shopify Push ── */}
      {stores.length > 0 && (
        <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 16, marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#166534', marginBottom: 4 }}>Push to Shopify</div>
              <div style={{ fontSize: 12, color: '#15803d' }}>
                {unpushedToSelected.length > 0
                  ? <>{unpushedToSelected.length} approved product{unpushedToSelected.length !== 1 ? 's' : ''} ready to push as draft ({pushedToSelected.length} already in Shopify)</>
                  : approved.length === 0
                    ? 'No fully approved products yet'
                    : <>All {approved.length} approved products are in Shopify for this store</>
                }
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button
                style={{ ...btn('secondary', { fontSize: 12, padding: '5px 8px', flexShrink: 0 }), background: downloaded._approved ? '#d1fae5' : C.fa, border: `1px solid ${downloaded._approved ? '#6ee7b7' : '#86efac'}`, color: downloaded._approved ? '#065f46' : '#166534' }}
                onClick={() => { exportAll(); setDownloaded(prev => ({ ...prev, _approved: true })); }}
                disabled={exportable.length === 0}
              >
                {downloaded._approved ? '✓ Downloaded' : '↓ Download CSV'}
              </button>
              <select
                value={selectedStoreTerr}
                onChange={e => setSelectedStoreTerr(e.target.value)}
                title="Territory for this store: sets size translations on push"
                style={{ fontSize: 12, padding: '5px 8px', border: `1px solid #86efac`, borderRadius: 6, background: C.white, color: C.text }}
              >
                <option value="ALL">All stores</option>
                {territories.filter(t => t.code !== 'STG').map(t => (
                  <option key={t.code} value={t.code}>{t.flag} {t.name}</option>
                ))}
              </select>
              {stores.length > 0 && (
                <button
                  style={{ ...btn('secondary', { fontSize: 12, padding: '5px 8px' }), borderColor: '#86efac', color: '#166534' }}
                  onClick={syncAllStores}
                  disabled={shopifySyncing || syncingAll}
                >
                  {shopifySyncing || syncingAll ? 'Syncing…' : '↻ Sync'}
                </button>
              )}
              <button
                title="Push order: controls which product gets the newest Shopify timestamp (appears first in new→old sort)"
                style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #86efac', borderRadius: 6, background: C.white, color: '#166534', cursor: 'pointer' }}
                onClick={() => setPushOrder(o => o === 'top' ? 'bottom' : 'top')}
              >
                {pushOrder === 'top' ? '↓ Top → Bottom' : '↑ Bottom → Top'}
              </button>
              {pushedToSelected.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#166534', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={updateExisting} onChange={e => setUpdateExisting(e.target.checked)} style={{ cursor: 'pointer' }} />
                  Update {pushedToSelected.length} existing
                </label>
              )}
              <button
                style={{ ...btn('primary', { fontSize: 12, padding: '5px 8px' }), background: approved.length === 0 ? C.fa : (unpushedToSelected.length === 0 && !updateExisting) ? '#d1fae5' : '#16a34a', border: `1px solid ${approved.length === 0 ? C.e8 : (unpushedToSelected.length === 0 && !updateExisting) ? '#6ee7b7' : '#15803d'}`, color: approved.length === 0 ? C.muted : (unpushedToSelected.length === 0 && !updateExisting) ? '#065f46' : C.white }}
                onClick={pushToShopify}
                disabled={shopifyPushing || (updateExisting ? approved.length === 0 : unpushedToSelected.length === 0)}
              >
                {shopifyPushing ? 'Pushing…' : approved.length === 0 ? 'Nothing to push' : updateExisting ? `↑ Push + update ${approved.length} to Shopify` : unpushedToSelected.length === 0 ? '✓ All pushed' : `↑ Push ${unpushedToSelected.length} to Shopify`}
              </button>
            </div>
          </div>
          {syncError && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #86efac', fontSize: 12, color: '#dc2626' }}>
              ✗ Sync error: {syncError}
            </div>
          )}
          {shopifyPushResult && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #86efac' }}>
              {shopifyPushResult.pushed > 0 && (
                <div style={{ fontSize: 12, color: '#166534', marginBottom: shopifyPushResult.errors.length ? 6 : 0 }}>
                  ✓ {shopifyPushResult.pushed} product{shopifyPushResult.pushed !== 1 ? 's' : ''} pushed to Shopify as draft
                  {currentUser?.email && <span style={{ marginLeft: 8, color: '#15803d' }}>by {currentUser.email}</span>}
                  <span style={{ marginLeft: 8, color: '#15803d' }}>{new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  {(() => { const conn = pushedToSelected[0] && Object.values(pushedToSelected[0].shopifyConnections || {}).find(c => c?.adminUrl); return conn ? <a href={conn.adminUrl.replace(/\/\d+$/, '')} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: '#15803d', textDecoration: 'underline' }}>View in Shopify →</a> : null; })()}
                </div>
              )}
              {shopifyPushResult.errors.map((e, i) => (
                <div key={i} style={{ fontSize: 12, color: '#dc2626' }}>✗ {e.error || JSON.stringify(e)}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {stores.length === 0 && (
        <div style={{ background: C.fa, border: `1px dashed ${C.d0}`, borderRadius: 10, padding: 16, marginBottom: 20, fontSize: 12, color: C.muted, textAlign: 'center' }}>
          No Shopify stores connected. Add one in the <strong>Team</strong> tab to push products directly
        </div>
      )}

      {/* Warehouse / Quick Export */}
      <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 4 }}>Warehouse draft export</div>
            <div style={{ fontSize: 12, color: '#78350f' }}>
              {warehouseReady.length} product{warehouseReady.length !== 1 ? 's' : ''} with Handle + Title + Variant SKU, exports as <strong>draft</strong> (never visible to customers). Use to create receiving records in Shopify before the build is complete.
              {(() => { const terrs = warehouseTerritories(warehouseReady, territories); return terrs.length > 1 ? <> Downloads {terrs.length} files: {terrs.map(t => t.code).join(', ')}.</> : null; })()}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            {[
              { label: 'Images', val: warehouseIncludeImages, set: setWarehouseIncludeImages },
              { label: 'Descriptions', val: warehouseIncludeBody, set: setWarehouseIncludeBody },
            ].map(({ label, val, set }) => (
              <label key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#92400e', cursor: 'pointer' }}>
                <input type="checkbox" checked={val} onChange={e => { set(e.target.checked); setDownloaded(prev => ({ ...prev, _warehouse: false })); }} />
                {label}
              </label>
            ))}
            <button
              style={{ ...btn('secondary', { fontSize: 12, padding: '5px 8px', flexShrink: 0 }), background: downloaded._warehouse ? '#d1fae5' : '#fef3c7', border: `1px solid ${downloaded._warehouse ? '#6ee7b7' : '#fcd34d'}`, color: downloaded._warehouse ? '#065f46' : '#92400e' }}
              onClick={doExportWarehouse}
              disabled={warehouseReady.length === 0}
            >
              {downloaded._warehouse ? '✓ Downloaded' : '↓ Download draft CSV'}
            </button>
            {stores.length > 0 && (<>
              <select
                value={warehouseStoreTerr}
                onChange={e => setWarehouseStoreTerr(e.target.value)}
                title="Territory for this store: sets size translations on push"
                style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #fcd34d', borderRadius: 6, background: C.white, color: C.text }}
              >
                <option value="ALL">All stores</option>
                {territories.filter(t => t.code !== 'STG').map(t => (
                  <option key={t.code} value={t.code}>{t.flag} {t.name}</option>
                ))}
              </select>
              <button
                style={{ ...btn('secondary', { fontSize: 12, padding: '5px 8px' }), borderColor: '#fcd34d', color: '#92400e' }}
                onClick={syncWarehouseStores}
                disabled={warehouseSyncing}
              >
                {warehouseSyncing ? 'Syncing…' : '↻ Sync'}
              </button>
              <button
                title="Push order: controls which product gets the newest Shopify timestamp (appears first in new→old sort)"
                style={{ fontSize: 12, padding: '5px 8px', border: '1px solid #fcd34d', borderRadius: 6, background: C.white, color: '#92400e', cursor: 'pointer' }}
                onClick={() => setWarehousePushOrder(o => o === 'top' ? 'bottom' : 'top')}
              >
                {warehousePushOrder === 'top' ? '↓ Top → Bottom' : '↑ Bottom → Top'}
              </button>
              {warehousePushedToStore.length > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#92400e', cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={updateWarehouseExisting} onChange={e => setUpdateWarehouseExisting(e.target.checked)} style={{ cursor: 'pointer' }} />
                  Update {warehousePushedToStore.length} existing
                </label>
              )}
              <button
                style={{ ...btn('primary', { fontSize: 12, padding: '5px 8px' }), background: warehouseReady.length === 0 ? C.fa : (warehouseUnpushedToStore.length === 0 && !updateWarehouseExisting) ? '#d1fae5' : '#d97706', border: `1px solid ${warehouseReady.length === 0 ? C.e8 : (warehouseUnpushedToStore.length === 0 && !updateWarehouseExisting) ? '#6ee7b7' : '#b45309'}`, color: warehouseReady.length === 0 ? C.muted : (warehouseUnpushedToStore.length === 0 && !updateWarehouseExisting) ? '#065f46' : C.white }}
                onClick={pushWarehouseToShopify}
                disabled={warehousePushing || (updateWarehouseExisting ? warehouseReady.length === 0 : warehouseUnpushedToStore.length === 0)}
              >
                {warehousePushing ? 'Pushing…' : warehouseReady.length === 0 ? 'Nothing to push' : updateWarehouseExisting ? `↑ Push + update ${warehouseReady.length} to Shopify` : warehouseUnpushedToStore.length === 0 ? '✓ All pushed' : `↑ Push ${warehouseUnpushedToStore.length} to Shopify`}
              </button>
            </>)}
          </div>
        </div>
        {warehouseSyncError && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #fcd34d', fontSize: 12, color: '#dc2626' }}>
            ✗ Sync error: {warehouseSyncError}
          </div>
        )}
        {warehousePushResult && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #fcd34d' }}>
            {warehousePushResult.pushed > 0 && (
              <div style={{ fontSize: 12, color: '#92400e', marginBottom: warehousePushResult.errors.length ? 6 : 0 }}>
                ✓ {warehousePushResult.pushed} product{warehousePushResult.pushed !== 1 ? 's' : ''} pushed to Shopify as warehouse draft
                {currentUser?.email && <span style={{ marginLeft: 8, color: '#78350f' }}>by {currentUser.email}</span>}
                <span style={{ marginLeft: 8, color: '#78350f' }}>{new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                {(() => { const conn = warehousePushedToStore[0] && Object.values(warehousePushedToStore[0].warehouseConnections || {}).find(c => c?.adminUrl); return conn ? <a href={conn.adminUrl.replace(/\/\d+$/, '')} target="_blank" rel="noreferrer" style={{ marginLeft: 8, color: '#78350f', textDecoration: 'underline' }}>View in Shopify →</a> : null; })()}
              </div>
            )}
            {warehousePushResult.errors.map((e, i) => (
              <div key={i} style={{ fontSize: 12, color: '#dc2626' }}>✗ {e.error || JSON.stringify(e)}</div>
            ))}
          </div>
        )}
      </div>

      {!exportAsIs && errors.length > 0 && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#dc2626', marginBottom: 8 }}>Validation errors ({errors.length} products)</div>
          {errors.map((e, i) => (
            <div key={i} style={{ fontSize: 12, color: '#7f1d1d', marginBottom: 3 }}>
              <strong>{e.product}</strong>: {e.issues.join(', ')}
            </div>
          ))}
        </div>
      )}

      {!exportAsIs && warnings.length > 0 && (
        <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#92400e', marginBottom: 8 }}>Warnings ({warnings.length} products)</div>
          {warnings.map((w, i) => (
            <div key={i} style={{ fontSize: 12, color: '#78350f', marginBottom: 3 }}>
              <strong>{w.product}</strong>: {w.issues.join(', ')}
            </div>
          ))}
        </div>
      )}

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer', background: exportAsIs ? '#fffbeb' : C.fa, border: `1px solid ${exportAsIs ? '#fcd34d' : C.e8}`, borderRadius: 10, padding: '12px 16px' }}>
        <input type="checkbox" checked={exportAsIs} onChange={e => setExportAsIs(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer', marginTop: 1 }} />
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Export as-is: ignore approval &amp; required fields</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
            Includes every product that has a handle, SKU or title, even if its sections aren't approved or some fields are blank. Empty cells simply export blank.
          </div>
        </div>
      </label>

      {exportable.length > 0 ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button style={btn('primary')} onClick={exportAll}>
              Export all {territories.length} territories + 3 sheets
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {territories.map(t => (
              <button key={t.code} onClick={() => doExport(t)} style={{
                ...btn('secondary', { justifyContent: 'space-between', padding: '12px 14px', width: '100%' }),
                background: downloaded[t.code] ? '#d1fae5' : C.fa,
                border: `1px solid ${downloaded[t.code] ? '#6ee7b7' : C.e8}`,
                color: downloaded[t.code] ? '#065f46' : C.text,
              }}>
                <span>{t.flag} {t.name} ({t.currency})</span>
                <span style={{ fontSize: 14 }}>{downloaded[t.code] ? '✓' : '↓'}</span>
              </button>
            ))}
          </div>

          {/* Supplementary sheets (territory-agnostic) */}
          <div style={{ marginTop: 24, borderTop: `1px solid ${C.e8}`, paddingTop: 20 }}>
            <div style={{ fontFamily: F.display, fontSize: 16, color: C.text, marginBottom: 12 }}>
              Supplementary sheets
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>
              These don't vary by territory. Upload once to Shopify alongside any territory's product build.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              {[
                { key: '_meta',  label: 'Metafields',          fn: doExportMeta },
                { key: '_fit',   label: 'Fit & Fabric',        fn: doExportFit  },
                { key: '_care',  label: 'Care Instructions',   fn: doExportCare },
              ].map(({ key, label, fn }) => (
                <button key={key} onClick={fn} style={{
                  ...btn('secondary', { justifyContent: 'space-between', padding: '12px 14px', width: '100%' }),
                  background: downloaded[key] ? '#d1fae5' : C.fa,
                  border: `1px solid ${downloaded[key] ? '#6ee7b7' : C.e8}`,
                  color: downloaded[key] ? '#065f46' : C.text,
                }}>
                  <span>{label}</span>
                  <span style={{ fontSize: 14 }}>{downloaded[key] ? '✓' : '↓'}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div style={{ padding: 40, textAlign: 'center', color: C.muted, fontSize: 14, background: C.fa, borderRadius: 10, border: `1px solid ${C.e8}` }}>
          {exportAsIs
            ? <>No products have any content to export yet.<br />Add a handle, SKU or title to at least one product.</>
            : <>No products are fully approved yet.<br />Approve all 6 sections on a product, or tick <strong>Export as-is</strong> above to export regardless.</>}
        </div>
      )}
    </div>
  );
}
